import { useMemo } from 'react';
import {
  LocationCaseRecord,
  LocationInventoryRecord,
  LocationRecordSection
} from '../../../Api/Locations/locationsApi';
import { Permission } from '../../../store/GeneralStore';
import { fetchCases } from '../../../store/SupportCase';
import { Account, SupportCasesItem } from '../../../types';
import { eligibleServices } from '../../Compounds/CaseCreation/eligibleServices';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { activeFirst, countedTitle, moreRecordsNote } from '../../Compounds/RelatedRecordsTable/relatedRecords';
import { SupportCasesCard } from '../../Compounds/SupportCasesCard/SupportCasesCard';
import { SupportCaseWizard } from '../../Compounds/Wizards/SupportCaseWizard';

export interface LocationCasesSectionProps {
  cases: LocationRecordSection<LocationCaseRecord>;
  count: number | null | undefined;
  created: LocationCaseRecord[];
  onCaseCreated: (case_: SupportCasesItem, account: Account) => void;
  address: string;
  accountId: string;
  services: LocationInventoryRecord[];
}

const NO_SUPPORTED_SERVICE =
  'None of the services listed at this location is supported by UPSTACK, so a case cannot be opened from here.';

const addToCount = (count: number | null | undefined, extra: number) =>
  typeof count === 'number' ? count + extra : count;

export function LocationCasesSection({
  cases,
  count,
  created,
  onCaseCreated,
  address,
  accountId,
  services
}: LocationCasesSectionProps) {
  const supported = useMemo(() => eligibleServices(services), [services]);
  const locationScope = useMemo(() => ({ address, accountId, services: supported }), [address, accountId, supported]);
  const unlisted = created.filter((case_) => !cases.records.some((record) => record.id === case_.id));
  const shown = { ...cases, records: [...unlisted, ...cases.records], total: cases.total + unlisted.length };

  return (
    <SupportCasesCard
      title={countedTitle('Support Cases', addToCount(count, unlisted.length))}
      cases={activeFirst(shown.records)}
      emptyText="No cases at this location"
      note={moreRecordsNote(shown)}
      testId="location-cases-table"
      cardActions={
        <PermissibleRender requiredPermissions={[Permission.CREATE_CASES_FROM_INVENTORY]}>
          <SupportCaseWizard
            ctaClassName="text-grey-12 text-sm"
            location={locationScope}
            onCaseCreated={onCaseCreated}
            onFormSubmissionHandler={async () => fetchCases(true)}
            disabled={!supported.length}
            disabledMessage={NO_SUPPORTED_SERVICE}
          />
        </PermissibleRender>
      }
    />
  );
}
